/**
 * Question World Utilities
 * Helper functions for the Question World module
 */

/**
 * Generate unique key for a question
 * @param {Object} question - Question object
 * @param {number} index - Question index
 * @returns {string} - Unique key
 */
export const getQuestionKey = (question, index) => {
  return (
    question?._id ||
    question?.questionId?._id ||
    question?.question?._id ||
    question?.id ||
    `question-${index}`
  );
};

/**
 * Compare two arrays for equality (order-independent)
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @returns {boolean} - True if arrays are equal
 */
export const compareArray = (arr1, arr2) => {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  for (let i = 0; i < sorted1.length; i++) {
    let value1 = sorted1[i];
    let value2 = sorted2[i];
    if (typeof value1 === 'string') value1 = value1.normalize();
    if (typeof value2 === 'string') value2 = value2.normalize();
    if (value1 !== value2) return false;
  }
  return true;
};

/**
 * Check if answer is correct based on question type
 * @param {Object} question - Question object
 * @param {any} userAnswer - User's answer
 * @returns {boolean} - True if correct
 */
export const isAnswerCorrect = (question, userAnswer) => {
  if (!question || !userAnswer) return false;

  const correctAnswer = question.answer || [];
  const questionType = question.type;

  // For MCQ and checkbox: compare arrays
  if (questionType === 'MCQ' || questionType === 'checkbox') {
    const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
    const correctAnsArray = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
    return compareArray(userAnsArray, correctAnsArray);
  }

  // For MCA: compare each option individually
  if (questionType === 'MCA') {
    const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [];
    const correctAnsArray = Array.isArray(correctAnswer) ? correctAnswer : [];
    if (userAnsArray.length !== correctAnsArray.length) return false;
    
    for (let i = 0; i < correctAnsArray.length; i++) {
      if (userAnsArray[i] !== correctAnsArray[i]) {
        return false;
      }
    }
    return true;
  }

  // For shortAns and paragraph: text comparison (case-insensitive, trimmed)
  if (questionType === 'shortAns' || questionType === 'paragraph') {
    const userAnsText = (Array.isArray(userAnswer) ? userAnswer[0] : userAnswer || '').toString().trim().toLowerCase();
    const correctAnsText = (Array.isArray(correctAnswer) ? correctAnswer[0] : correctAnswer || '').toString().trim().toLowerCase();
    return userAnsText === correctAnsText;
  }

  return false;
};

/**
 * Calculate progress percentage
 * @param {number} answered - Number of answered questions
 * @param {number} total - Total questions
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateProgress = (answered, total) => {
  if (!total || total === 0) return 0;
  return Math.round((answered / total) * 100);
};

/**
 * Check if string contains math/katex expressions
 * @param {string} value - String to check
 * @returns {boolean} - True if contains math
 */
export const shouldUseKatex = (value) => {
  if (!value || typeof value !== 'string') return false;
  const regex = /\^|\\|\+|_|\{|\}|\$/;
  return value.search(regex) >= 0;
};

/**
 * Get student data from localStorage
 * @returns {Object} - Student object
 */
export const getStudentFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('student') || '{}');
  } catch (error) {
    return {};
  }
};

/**
 * Format duration in minutes to readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration
 */
export const formatDuration = (minutes) => {
  if (!minutes) return 'N/A';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

